import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpBodyRespModel} from '../../http-body-resp/model';
import {generateHttpParams} from '../../util';
import { HttpParamPaginationModel } from "../../http-body-req/model/http-param-pagination.model";

@Injectable({
	providedIn: 'root',
})
export class BaseService {
	constructor(private http: HttpClient) {
	}

	private static mapObjectData(dto: any, responseModel: any) {
		if (Object.entries(dto).length === 0) {
			return null;
		}

		return new responseModel().convert(dto);
	}

	public getBlobData(url: string, requestParamModel?: any): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		return this.http.get(url, {
			params,
			responseType: 'blob',
		});
	}

	public getBlobDataWithMap(
		url: string,
		responseModel?: any,
		requestParamModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		return this.http
			.get(url, {
				params,
				responseType: 'blob',
				observe: 'response',
			})
			.pipe(
				map((value) => {
					const contentDisposition =
						value.headers.get('content-disposition') || '';
					const matches = /filename=([^;]+)/gi.exec(contentDisposition);
					const fileName = (matches[1] || 'untitled').trim();

					return responseModel
						? new responseModel().convert({
							fileName,
							body: value.body,
						})
						: {
							fileName,
							body: value.body,
						};
				})
			);
	}

	public getData(
		url: string,
		responseModel: any,
		requestParamModel?: any,
		isArray?: boolean
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		return this.http.get(url, {params}).pipe(
			map(
				(model: HttpBodyRespModel): HttpBodyRespModel =>
					new HttpBodyRespModel().convert(model)
			),
			map((model: HttpBodyRespModel): any => {
				return responseModel
					? isArray
						? this.mapArrayData(model.data, responseModel)
						: BaseService.mapObjectData(model.data, responseModel)
					: model;
			})
		);
	}

	public getDataPaging(
		url: string,
		responseModel: any,
		requestParamModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		return this.http.get(url, {params}).pipe(
			map(
				(model: HttpBodyRespModel): HttpBodyRespModel =>
					new HttpBodyRespModel().convert(model)
			),
			map((model: HttpBodyRespModel): any => {
				if (responseModel) {
					model.data = this.mapArrayData(model.data, responseModel);
				}

				return model;
			})
		);
	}

	public getDataResponsePaging(
		url: string,
		responseModel: any,
		requestParamModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		return this.http.get(url, {params}).pipe(
			map((model: HttpBodyRespModel): any => {

				model = this.mapResponseModel(model, responseModel);

				return model;
			})
		);
	}

	public postBlobData(
		url: string,
		requestBodyModel: any,
		requestParamModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		const body =
			requestBodyModel && requestBodyModel.convert
				? requestBodyModel.convert()
				: requestBodyModel;

		return this.http.post(url, body, {
			params,
			responseType: 'blob',
		});
	}

	public postData(
		url: string,
		requestBodyModel: any,
		responseModel?: any,
		requestParamModel?: any,
		isArray?: boolean
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		const body = requestBodyModel ? requestBodyModel.convert() : null;
		return this.http.post(url, body, {params}).pipe(
			map(
				(model: HttpBodyRespModel): HttpBodyRespModel =>
					new HttpBodyRespModel().convert(model)
			),
			map((model: HttpBodyRespModel): any => {
				return responseModel
					? isArray
						? this.mapArrayData(model.data, responseModel)
						: BaseService.mapObjectData(model.data, responseModel)
					: model;
			})
		);
	}

	public putData(
		url: string,
		requestBodyModel: any,
		responseModel?: any,
		requestParamModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		const body = requestBodyModel ? requestBodyModel.convert() : null;

		return this.http.put(url, body, {params}).pipe(
			map(
				(model: HttpBodyRespModel): HttpBodyRespModel =>
					new HttpBodyRespModel().convert(model)
			),
			map((model: HttpBodyRespModel): any => {
				return responseModel
					? BaseService.mapObjectData(model.data, responseModel)
					: model;
			})
		);
	}

	public put(
		url: string,
		requestBody: any
	): Observable<HttpBodyRespModel> {

		return this.http.put<HttpBodyRespModel>(url, requestBody).pipe(
			map((resp: HttpBodyRespModel) => {
				return resp
			})
		)
	}

	public patchData(
		url: string,
		requestBodyModel: any,
		responseModel?: any,
		requestParamModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		return this.http.patch(url, requestBodyModel.convert(), {params}).pipe(
			map(
				(model: HttpBodyRespModel): HttpBodyRespModel =>
					new HttpBodyRespModel().convert(model)
			),
			map((model: HttpBodyRespModel): any => {
				return responseModel
					? BaseService.mapObjectData(model.data, responseModel)
					: model;
			})
		);
	}

	public deleteData(
		url: string,
		requestParamModel: any,
		responseModel?: any,
		requestBodyModel?: any
	): Observable<any> {
		const params = requestParamModel
			? generateHttpParams(requestParamModel.convert())
			: null;

		const options = {
			params: params,
			body: requestBodyModel ? requestBodyModel.convert() : null,
		};

		return this.http.delete(url, options).pipe(
			map(
				(model: HttpBodyRespModel): HttpBodyRespModel =>
					new HttpBodyRespModel().convert(model)
			),
			map((model: HttpBodyRespModel): any => {
				return responseModel
					? BaseService.mapObjectData(model.data, responseModel)
					: model;
			})
		);
	}

	private mapArrayData(dtos: any[], responseModel: any) {
		if (dtos === null) {
			return null;
		}

		return dtos.reduce((result, each) => {
			const model = new responseModel();
			result.push(model.convert(each));

			return result;
		}, []);
	}

	private mapResponseModel(dtos: any, responseModel: any) {
		return {
			...dtos,
			data: dtos.data
		}
	}
}
